<template>
  <header class="approve-header gm-card rounded-xl pa-5 pa-md-6">
    <div class="approve-header__main d-flex flex-column flex-lg-row align-lg-center justify-space-between ga-4">
      <div class="approve-header__lead d-flex flex-nowrap align-start ga-4 min-width-0">
        <v-btn variant="text" density="comfortable" class="approve-header__back px-1 flex-shrink-0" @click="goBack">
          <v-icon start size="20">mdi-chevron-left</v-icon>
          返回
        </v-btn>
        <div class="approve-header__title-block min-width-0 flex-grow-1">
          <div class="d-flex flex-wrap align-center ga-3 mb-1">
            <h1 class="approve-header__title text-h6 font-weight-semibold mb-0">{{ station.name }}</h1>
            <v-chip v-if="station.status" size="small" variant="flat" :color="statusColor"
              class="approve-header__status-chip font-weight-medium">
              {{ station.status }}
            </v-chip>
          </div>
          <p class="approve-header__id text-body-2 mb-0">电站 ID：{{ station.id }}</p>
        </div>
      </div>

      <div class="approve-header__meta flex-grow-1 d-flex flex-wrap ga-5 ga-md-6">
        <div v-for="item in metaItems" :key="item.label" class="approve-header__meta-col">
          <span class="approve-header__meta-label text-caption text-medium-emphasis">{{ item.label }}</span>
          <span class="approve-header__meta-value text-body-2 font-weight-medium" :title="item.primary">
            {{ item.primary }}
          </span>
          <span v-if="item.secondary" class="approve-header__meta-sub text-caption text-medium-emphasis"
            :title="item.secondary">
            {{ item.secondary }}
          </span>
        </div>
      </div>

      <div class="approve-header__actions d-flex flex-wrap align-center justify-lg-end ga-2 ga-sm-3 flex-shrink-0">
        <template v-if="isStepEditing">
          <v-btn color="primary" variant="tonal" rounded="lg" size="large" class="text-none approve-header__btn"
            :disabled="saving" @click="$emit('cancel-edit')">
            取消编辑
          </v-btn>
          <v-btn color="primary" variant="flat" rounded="lg" size="large"
            class="text-none approve-header__btn approve-header__btn--primary" prepend-icon="mdi-content-save-outline"
            :loading="saving" @click="$emit('save-edit')">
            保存
          </v-btn>
        </template>
        <template v-else-if="canEdit">
          <v-btn color="primary" variant="flat" rounded="lg" size="large" class="text-none approve-header__btn"
            prepend-icon="mdi-pencil-outline" @click="$emit('start-edit')">
            编辑
          </v-btn>
        </template>
        <v-btn v-if="canApprove" class="approve-header__btn approve-header__btn-reject text-none" rounded="lg"
          size="large" prepend-icon="mdi-close-circle-outline" @click="$emit('reject')">
          驳回
        </v-btn>
        <v-btn v-if="canApprove" color="success" variant="flat" rounded="lg" size="large"
          class="text-none approve-header__btn approve-header__btn--success" prepend-icon="mdi-check-circle-outline"
          @click="$emit('approve')">
          投运
        </v-btn>
        <v-btn v-if="canResubmit" color="success" variant="flat" rounded="lg" size="large"
          class="text-none approve-header__btn approve-header__btn--success" prepend-icon="mdi-send-outline"
          :disabled="isStepEditing" :loading="resubmitting" @click="$emit('resubmit')">
          提交
        </v-btn>
      </div>
    </div>
  </header>
</template>

<script setup>
import { computed } from 'vue'
import { useRouter } from 'vue-router'

const props = defineProps({
  station: {
    type: Object,
    required: true,
  },
  isStepEditing: {
    type: Boolean,
    default: false,
  },
  saving: {
    type: Boolean,
    default: false,
  },
  // 是否显示「驳回 / 投运」按钮：申请者视角进入详情时置 false
  canApprove: {
    type: Boolean,
    default: true,
  },
  // 是否显示「编辑」按钮：审批已通过或审批记录尚未加载完成时置 false
  canEdit: {
    type: Boolean,
    default: true,
  },
  // 是否显示「提交」按钮：仅在申请者视角 + 已驳回状态下为 true
  canResubmit: {
    type: Boolean,
    default: false,
  },
  // 「提交」按钮 loading 态
  resubmitting: {
    type: Boolean,
    default: false,
  },
})

defineEmits(['reject', 'approve', 'start-edit', 'cancel-edit', 'save-edit', 'resubmit'])

const router = useRouter()

/** 状态颜色：审批中黄、已通过绿、已驳回红 */
const statusColor = computed(() => {
  const code = props.station?.statusCode
  if (code === 1) return 'success'
  if (code === 2) return 'error'
  return 'warning'
})

const metaItems = computed(() => [
  {
    label: '产品型号',
    primary: props.station.model,
    secondary: props.station.capacity,
  },
  {
    label: '所在区域',
    primary: props.station.region,
    secondary: props.station.location,
  },
  {
    label: '申请者',
    primary: props.station.applicant,
    secondary: null,
  },
  {
    label: '申请时间',
    primary: props.station.applyTime,
    secondary: null,
  },
])

function goBack() {
  router.back()
}
</script>

<style scoped lang="scss">
/* 与 station-add、variables.scss（gm-card / --color-*）对齐 */

.approve-header {
  box-shadow: 0 2px 8px rgba(8, 40, 87, 0.05);
}

.approve-header__lead {
  flex: 1 1 240px;
  min-width: 0;
}

.approve-header__back {
  color: var(--color-text) !important;
  opacity: 0.85;
  margin-top: 2px;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }
}

.approve-header__title-block {
  min-width: 0;
}

.approve-header__title {
  color: var(--color-text);
  letter-spacing: -0.01em;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 100%;
}

.approve-header__id {
  color: var(--color-primary);
  font-weight: 500;
}

.approve-header__status-chip {
  height: 24px;
}

.approve-header__meta-col {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  max-width: 260px;
  flex: 0 1 auto;
}

.approve-header__meta-label {
  line-height: 1.3;
}

.approve-header__meta-value {
  color: var(--color-text);
  line-height: 1.4;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.approve-header__meta-sub {
  line-height: 1.35;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 100%;
}

.approve-header__actions {
  width: 100%;
  padding-top: 4px;
  border-top: 1px solid var(--color-td-border);
}

.approve-header__btn {
  min-height: 40px !important;
  padding-inline: 16px !important;
  font-weight: 600 !important;
  font-size: 0.875rem !important;
  letter-spacing: 0.01em;
}

.approve-header__btn--primary {
  box-shadow: 0 4px 12px rgba(43, 87, 238, 0.22);
}

.approve-header__btn--success {
  box-shadow: 0 4px 12px rgba(54, 192, 112, 0.22);
}

.approve-header__btn-reject {
  background: #3d4466 !important;
  color: #fff !important;

  &:hover {
    background: color-mix(in srgb, #3d4466 88%, #000) !important;
  }
}

@media (min-width: 1280px) {
  .approve-header__actions {
    width: auto;
    min-width: min(100%, 320px);
    padding-top: 0;
    border-top: none;
    align-self: center;
  }
}

@media (max-width: 599px) {
  .approve-header__actions {
    .approve-header__btn {
      flex: 1 1 calc(50% - 6px);
      min-width: 0;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .approve-header__back {
    transition: none;
  }
}

/* ============ 小屏 1366x768 兼容：整行展示，不换行 ============ */
@media (max-width: 1440px) {
  .approve-header {
    padding: 16px !important;
  }

  .approve-header__main {
    gap: 12px !important;
    flex-wrap: nowrap !important;
  }

  .approve-header__lead {
    flex: 0 1 auto;
    gap: 10px !important;
  }

  .approve-header__meta {
    gap: 16px !important;
    flex-wrap: nowrap !important;
    flex: 1 1 0;
    min-width: 0;
  }

  .approve-header__meta-col {
    max-width: 180px;
  }

  .approve-header__btn {
    min-height: 36px !important;
    padding-inline: 12px !important;
    font-size: 13px !important;
  }
}

@media (max-width: 1366px) {
  .approve-header {
    padding: 12px !important;
  }

  .approve-header__main {
    gap: 8px !important;
  }

  .approve-header__lead {
    gap: 8px !important;
  }

  .approve-header__back {
    font-size: 12px !important;
    padding-inline: 4px !important;
  }

  .approve-header__title {
    font-size: 15px !important;
    max-width: 150px;
  }

  .approve-header__id {
    font-size: 11px !important;
  }

  .approve-header__status-chip {
    height: 20px !important;
    font-size: 11px !important;
  }

  .approve-header__meta {
    gap: 12px !important;
  }

  .approve-header__meta-col {
    max-width: 150px;
  }

  .approve-header__meta-label {
    font-size: 11px !important;
  }

  .approve-header__meta-value {
    font-size: 12px !important;
  }

  .approve-header__meta-sub {
    font-size: 11px !important;
  }

  .approve-header__actions {
    gap: 6px !important;
    flex-wrap: nowrap !important;
  }

  .approve-header__btn {
    min-height: 32px !important;
    padding-inline: 10px !important;
    font-size: 12px !important;
  }

  .approve-header__btn :deep(.v-icon) {
    font-size: 16px !important;
  }
}
</style>
